<template>
  <transition name="zoom-slide" mode="out-in">
    <div
      :key="zoomPosition"
      class="zoom-controls"
      :class="`zoom-${zoomPosition}`"
    >
      <!-- Header con porcentaje y controles principales -->
      <div class="zoom-header">
        <div class="zoom-info">
          <span class="zoom-percentage">{{ zoomPercentage }}%</span>
          <div class="zoom-actions">
            <button
              @click="zoomOut"
              :disabled="zoomLevel <= minZoom"
              class="zoom-action-btn"
              title="Alejar"
            >
              <i class="fas fa-minus"></i>
            </button>
            <button
              @click="zoomIn"
              :disabled="zoomLevel >= maxZoom"
              class="zoom-action-btn"
              title="Acercar"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div class="zoom-controls-actions">
          <button @click="resetZoom" class="zoom-reset-btn" title="Zoom 100%">
            <i class="fas fa-undo"></i>
          </button>
          <button
            @click="toggleZoomPosition"
            class="zoom-position-btn"
            title="Cambiar posición"
          >
            <i class="fas fa-exchange-alt"></i>
          </button>
        </div>
      </div>

      <!-- Slider de zoom -->
      <div class="zoom-slider">
        <input
          type="range"
          :min="minZoom"
          :max="maxZoom"
          :step="zoomStep"
          :value="zoomLevel"
          @input="setZoom(parseFloat($event.target.value))"
          class="zoom-range"
        />
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ZoomControls",
  props: {
    zoomLevel: {
      type: Number,
      required: true,
    },
    minZoom: {
      type: Number,
      default: 0.5,
    },
    maxZoom: {
      type: Number,
      default: 2,
    },
    zoomStep: {
      type: Number,
      default: 0.1,
    },
    zoomPosition: {
      type: String,
      default: "right",
      validator: (value) => ["left", "right"].includes(value),
    },
  },
  computed: {
    zoomPercentage() {
      return Math.round(this.zoomLevel * 100);
    },
  },
  mounted() {
    // Componente montado correctamente
  },
  methods: {
    zoomIn() {
      this.$emit("zoom-in");
    },
    zoomOut() {
      this.$emit("zoom-out");
    },
    resetZoom() {
      this.$emit("reset-zoom");
    },
    setZoom(level) {
      this.$emit("set-zoom", level);
    },
    toggleZoomPosition() {
      this.$emit("toggle-position");
    },
  },
};
</script>

<style scoped>
/* Controles de zoom  */
.zoom-controls {
  position: fixed;
  bottom: 30px;
  background: var(--lego-bg);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 200px;
  border: 1px solid #e1e5e9;
  .dark & {
    border-color: var(--lego-hoscuro);
  }
}

/* Posición derecha (por defecto) */
.zoom-right {
  right: 30px;
}

/* Posición izquierda */
.zoom-left {
  left: 30px;
}

/* Transiciones de Vue */
.zoom-slide-enter-active,
.zoom-slide-leave-active {
  transition: all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.zoom-slide-enter-from {
  opacity: 0;
  transform: translateX(30px) scale(0.9);
}

.zoom-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px) scale(0.9);
}

.zoom-slide-enter-to,
.zoom-slide-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

/* Header reorganizado */
.zoom-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.zoom-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.zoom-percentage {
  font-size: 16px;
  font-weight: 600;
  color: var(--lego-text);
  min-width: 45px;
}

.zoom-actions {
  display: flex;
  gap: 6px;
}

.zoom-action-btn {
  background: var(--lego-dbg);
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  color: var(--lego-text);
  transition: all 0.2s ease;
  font-size: 12px;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-action-btn:hover:not(:disabled) {
  background: #085ec5;
  color: white;
  transform: scale(1.05);
}

.zoom-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-controls-actions {
  display: flex;
  gap: 6px;
  min-width: 60px;
  justify-content: flex-end;
  margin-left: 6px;
}

.zoom-reset-btn,
.zoom-position-btn {
  background: var(--lego-dbg);
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  color: var(--lego-text);
  transition: all 0.2s ease;
  font-size: 12px;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.zoom-reset-btn:hover,
.zoom-position-btn:hover {
  background: #085ec5;
  color: white;
  transform: scale(1.05);
}

.zoom-slider {
  width: 100%;
  margin-top: 4px;
}

.zoom-range {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--lego-dbg);
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  border: none;
}

.zoom-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #085ec5;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border: 2px solid var(--lego-text);
}

.zoom-range::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.zoom-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #085ec5;
  cursor: pointer;
  border: 2px solid var(--lego-text);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.zoom-range::-moz-range-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.zoom-range::-webkit-slider-track {
  background: var(--lego-light-hover);
  border-radius: 3px;
  height: 6px;
}

.zoom-range::-moz-range-track {
  background: var(--lego-light-hover);
  border-radius: 3px;
  height: 6px;
  border: none;
}

/* Responsive para controles de zoom */
@media (max-width: 768px) {
  .zoom-controls {
    bottom: 20px;
    padding: 12px;
    min-width: 180px;
  }

  .zoom-right {
    right: 20px;
  }

  .zoom-left {
    left: 20px;
  }

  .zoom-info {
    gap: 8px;
  }

  .zoom-percentage {
    font-size: 14px;
    min-width: 40px;
  }

  .zoom-actions {
    gap: 6px;
  }

  .zoom-action-btn {
    min-width: 24px;
    height: 24px;
    padding: 4px 6px;
    font-size: 10px;
  }

  .zoom-controls-actions {
    gap: 6px;
  }

  .zoom-reset-btn,
  .zoom-position-btn {
    min-width: 24px;
    height: 24px;
    padding: 4px 6px;
    font-size: 10px;
  }

  .zoom-slider {
    width: 60px;
    margin-top: 0;
  }
}
</style>
